import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

import type { AnalyticsTableColumn } from '../AnalyticsTable.types'

export const useScroll = <T>(columns: AnalyticsTableColumn[], data: T[]) => {
  const ref = useRef<HTMLDivElement>(null)
  const [showLeftShadow, setShowLeftShadow] = useState(false)
  const [showRightShadow, setShowRightShadow] = useState(false)
  const [pinnedWidth, setPinnedWidth] = useState(0)

  const updateShadows = useCallback(() => {
    if (!ref.current) return

    const { scrollLeft, clientWidth, scrollWidth } = ref.current

    const firstCell = ref.current.querySelector('[data-column-index="0"]') as HTMLElement
    if (firstCell) {
      setPinnedWidth(firstCell.offsetWidth)
    }

    setShowLeftShadow(scrollLeft > 2)
    setShowRightShadow(scrollLeft + clientWidth < scrollWidth - 2)
  }, [])

  useLayoutEffect(() => {
    updateShadows()
  }, [updateShadows, columns, data])

  useEffect(() => {
    const handleResize = () => {
      setTimeout(() => {
        updateShadows()
      }, 0)
    }

    window.addEventListener('resize', handleResize)

    let resizeObserver: ResizeObserver | null = null

    if (ref.current) {
      resizeObserver = new ResizeObserver(() => {
        requestAnimationFrame(() => {
          updateShadows()
        })
      })

      resizeObserver.observe(ref.current)
    }

    return () => {
      window.removeEventListener('resize', handleResize)
      if (resizeObserver) {
        resizeObserver.disconnect()
      }
    }
  }, [updateShadows])

  return {
    ref,
    showLeftShadow,
    showRightShadow,
    pinnedWidth,
    updateShadows,
  }
}
